import { DefaultTheme, Interpolation, ThemeProps } from 'styled-components';

import IThemeInterface from '../../Theme/common';

import { calcBorderRadiusStyles, VALID_BORDER_RADIUS_DATA } from './calcBorderRadiusStyles';
import { calcBorderStyles, VALID_BORDER_DATA } from './calcBorderStyles';
import { calcBoxShadowStyles, VALID_SHADOW_DATA } from './calcBoxShadowStyles';
import { calcDimensionStyles, VALID_DIMENSION_DATA } from './calcDimensionStyles';
import { calcEdgeStyles, VALID_EDGE_STYLE_DATA } from './calcEdgeStyles';
import { calcFontSize, VALID_FONT_SIZE_DATA } from './calcFontSize';
import { calcOutlineStyles, VALID_OUTLINE_DATA } from './calcOutlineStyles';
import { VALID_MEDIA_STYLES } from './common';
import { makeMediaStyles } from './makeMediaStyles';
import { normalizeColorMedia, VALID_COLOR_DATA, VALID_COLOR_MEDIA_TYPES } from './normalizeColorMedia';

type VALID_DECLARATION_DATA =
  | VALID_EDGE_STYLE_DATA
  | VALID_BORDER_DATA
  | VALID_SHADOW_DATA
  | VALID_FONT_SIZE_DATA
  | VALID_DIMENSION_DATA
  | VALID_OUTLINE_DATA
  | VALID_COLOR_DATA
  | VALID_BORDER_RADIUS_DATA;

interface IData {
  'background-color'?: VALID_COLOR_DATA;
  border?: VALID_BORDER_DATA;
  'border-radius'?: VALID_BORDER_RADIUS_DATA;
  'box-shadow'?: VALID_SHADOW_DATA;
  color?: VALID_COLOR_DATA;
  'column-gap'?: VALID_EDGE_STYLE_DATA;
  fill?: VALID_COLOR_DATA;
  'font-size'?: VALID_FONT_SIZE_DATA;
  height?: VALID_DIMENSION_DATA;
  margin?: VALID_EDGE_STYLE_DATA;
  outline?: VALID_OUTLINE_DATA;
  padding?: VALID_EDGE_STYLE_DATA;
  stroke?: VALID_COLOR_DATA;
  'row-gap'?: VALID_EDGE_STYLE_DATA;
  width?: VALID_DIMENSION_DATA;
  '-webkit-text-fill-color': VALID_COLOR_DATA;
}

type DataKeys = keyof IData;

// tslint:disable:max-line-length
interface IOverloads {
  (theme: IThemeInterface, property: 'border', data: VALID_BORDER_DATA): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: 'border-radius', data: VALID_BORDER_RADIUS_DATA): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: 'box-shadow', data: VALID_SHADOW_DATA): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: 'font-size', data: VALID_FONT_SIZE_DATA): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: 'height' | 'width', data: VALID_DIMENSION_DATA): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: 'margin' | 'padding' | 'row-gap' | 'column-gap', data: VALID_EDGE_STYLE_DATA): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: 'outline', data: VALID_OUTLINE_DATA): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: VALID_COLOR_MEDIA_TYPES, data: VALID_COLOR_DATA | string): Interpolation<ThemeProps<DefaultTheme>>;
  (theme: IThemeInterface, property: string, data: VALID_MEDIA_STYLES): Interpolation<ThemeProps<DefaultTheme>>;
}
// tslint:enable:max-line-length

export const cssDeclaration: IOverloads = (
  theme: IThemeInterface,
  property: string,
  data: VALID_DECLARATION_DATA
) => {
  const dictionary: { [key in DataKeys]: (data: IData[DataKeys]) => Interpolation<ThemeProps<DefaultTheme>> } = {
    'background-color': normalizeColorMedia(theme, 'background-color'),
    border: calcBorderStyles(theme),
    'border-radius': calcBorderRadiusStyles(theme),
    'box-shadow': calcBoxShadowStyles(theme),
    color: normalizeColorMedia(theme, 'color'),
    'column-gap': calcEdgeStyles(theme, 'column-gap'),
    fill: normalizeColorMedia(theme, 'fill'),
    'font-size': calcFontSize(theme),
    height: calcDimensionStyles(theme, 'height'),
    margin: calcEdgeStyles(theme, 'margin'),
    outline: calcOutlineStyles(theme),
    padding: calcEdgeStyles(theme, 'padding'),
    stroke: normalizeColorMedia(theme, 'stroke'),
    'row-gap': calcEdgeStyles(theme, 'row-gap'),
    width: calcDimensionStyles(theme, 'width'),
    '-webkit-text-fill-color': normalizeColorMedia(theme, '-webkit-text-fill-color'),
  };

  return (
    dictionary[property as DataKeys] ?
      dictionary[property as DataKeys](data) :
      makeMediaStyles(property, data as VALID_MEDIA_STYLES)
  );
};
